import {
  Box,
  Button,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  Pagination,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import SendIcon from "@mui/icons-material/Send";
import { useAppSelector } from "src/redux/store";
import { getSeedlingQueries, addSeedlingQuery } from "src/utils/api/dashboard";

interface SeedlingQueryHistoryModalProps {
  seedlingRecordId: string;
  open: boolean;
  onClose: () => void;
  seedlingName?: string;
}

export const SeedlingQueryHistoryModal = ({
  seedlingRecordId,
  open,
  onClose,
  seedlingName = "",
}: SeedlingQueryHistoryModalProps) => {
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const limit = 5;

  const user = useAppSelector((state) => state.root.user.data);
  const userId = user?.userId;

  const {
    data: queriesResponse,
    isLoading: isLoadingQueries,
    isError: queryLoadFailed,
    refetch,
  } = useQuery(
    ["seedlingQueries", seedlingRecordId, page],
    async () => {
      const response = await getSeedlingQueries(seedlingRecordId, page, limit);
      return response?.data?.data;
    },
    {
      enabled: open && !!seedlingRecordId,
      refetchOnMount: true,
    }
  );

  const queriesData = queriesResponse?.data || [];
  const pagination = queriesResponse?.pagination || {
    totalPages: 1,
    currentPage: 1,
  };

  const { mutate: submitQuery, isLoading: isAddingQuery } = useMutation(
    async () => {
      if (!userId) {
        throw new Error("User ID not found");
      }

      const payload = {
        seedlingRecordId,
        userId,
        query: query.trim(),
      };

      const response = await addSeedlingQuery(payload);
      return response?.data;
    },
    {
      onSuccess: () => {
        setQuery("");
        setErrorMessage("");
        refetch();
      },
      onError: (error: any) => {
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add query";
        setErrorMessage(errorMsg);
      },
    }
  );

  const handleAddQuery = () => {
    if (!query.trim()) {
      setErrorMessage("Query cannot be empty");
      return;
    }
    if (!userId) {
      setErrorMessage("User information not available");
      return;
    }
    submitQuery();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUserLabelColor = (userType?: string) => {
    const colorMap: { [key: string]: string } = {
      ADMIN: "#d32f2f",
      NODAL_OFFICER: "#f57c00",
      IMPLEMENTING_OFFICER: "#388e3c",
      APPROVER: "#d32f2f",
      REVIEWER: "#d32f2f",
      LO: "#1976d2",
    };
    return colorMap[userType?.toUpperCase() || ""] || "#757575";
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "18px" }}>
        Seedling Query History {seedlingName ? `- ${seedlingName}` : ""}
      </DialogTitle>
      <DialogContent sx={{ minHeight: "450px", maxHeight: "600px" }}>
        {queryLoadFailed && (
          <Alert severity="error">
            Unable to load query history. Please try again.
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: "12px" }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: "20px",
            maxHeight: "330px",
            overflowY: "auto",
            paddingRight: "8px",
          }}
        >
          {isLoadingQueries ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : queriesData && queriesData.length > 0 ? (
            queriesData.map((item: any) => (
              <Paper
                key={item.id}
                sx={{
                  padding: "16px",
                  backgroundColor: item.name ? "#e8f5e9" : "#f5f5f5",
                  borderLeft: `4px solid ${getUserLabelColor(
                    item.userSubType || item.user?.userType
                  )}`,
                  borderRadius: "4px",
                  marginBottom: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      {item.name || item.user?.name || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: getUserLabelColor(
                          item.userSubType || item.user?.userType
                        ),
                        fontSize: "11px",
                      }}
                    >
                      {item.userSubType || item.user?.userType || "User"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    {formatDate(item.created_at)}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#555",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {item.query}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#999",
                padding: "40px 0",
              }}
            >
              No queries yet for this seedling.
            </Typography>
          )}
        </Box>

        {pagination.totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: "8px" }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Type your query or response here..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={
              isAddingQuery ||
              isLoadingQueries ||
              queryLoadFailed ||
              user?.role === "Approver-view-only"
            }
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button
          onClick={handleAddQuery}
          variant="contained"
          color="primary"
          disabled={
            isAddingQuery ||
            isLoadingQueries ||
            queryLoadFailed ||
            !query.trim() ||
            user?.role === "Approver-view-only"
          }
          startIcon={
            isAddingQuery ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
        >
          Send Query
        </Button>
      </DialogActions>
    </>
  );
};
